/* eslint-disable react/no-multi-comp */
import React, {
  CSSProperties,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  GroupedVirtuoso,
  GroupedVirtuosoProps,
  Virtuoso,
  VirtuosoProps,
} from 'react-virtuoso'
import { ListRange, VirtuosoHandle } from 'react-virtuoso/dist'
import * as R from 'ramda'

import { INFINITE_LIST_BATCH_LOAD_COUNT } from '../../constants/defaults'
import { Nil } from '../../types'
import { resizeArray } from '../../utils'

const scanForUnloadedRanges = ({
  isItemLoaded,
  itemCount,
  minimumBatchSize,
  startIndex,
  stopIndex,
}: {
  isItemLoaded: (index: number) => boolean
  itemCount: number
  minimumBatchSize: number
  startIndex: number
  stopIndex: number
}): number[] => {
  const unloadedRanges = []

  let rangeStartIndex = null
  let rangeStopIndex = null

  for (let index = startIndex; index <= stopIndex; index++) {
    const loaded = isItemLoaded(index)

    if (!loaded) {
      rangeStopIndex = index
      if (rangeStartIndex === null) {
        rangeStartIndex = index
      }
    } else if (rangeStopIndex !== null && rangeStartIndex !== null) {
      unloadedRanges.push(rangeStartIndex, rangeStopIndex)

      rangeStartIndex = null
      rangeStopIndex = null
    }
  }

  // If rangeStopIndex is not null it means we haven't ran out of unloaded rows.
  // Scan forward to try filling our minimumBatchSize.
  if (rangeStopIndex !== null && rangeStartIndex !== null) {
    const potentialStopIndex = Math.min(
      Math.max(rangeStopIndex, rangeStartIndex + minimumBatchSize - 1),
      itemCount - 1,
    )

    for (let index = rangeStopIndex + 1; index <= potentialStopIndex; index++) {
      if (!isItemLoaded(index)) {
        rangeStopIndex = index
      } else {
        break
      }
    }

    unloadedRanges.push(rangeStartIndex, rangeStopIndex)
  }

  // Check to see if our first range ended prematurely.
  // In this case we should scan backwards to try filling our minimumBatchSize.
  if (unloadedRanges.length > 0) {
    while (
      unloadedRanges[1] - unloadedRanges[0] + 1 < minimumBatchSize &&
      unloadedRanges[0] > 0
    ) {
      const index: number = unloadedRanges[0] - 1

      if (!isItemLoaded(index)) {
        unloadedRanges[0] = index
      } else {
        break
      }
    }
  }

  return unloadedRanges
}

const Footer = () => <div style={{ height: '1px' }} />

export interface InfiniteLoaderListProps
  extends Omit<VirtuosoProps<any, any>, 'itemSize' | 'children'> {
  readonly Header?: () => ReactElement<any, any>
  readonly children?: (item: any, index: number) => React.ReactNode
  readonly className?: string
  readonly disableInitialAutoScroll?: boolean
  readonly firstItemMargin?: number
  readonly focusedListItem?: number | Nil
  readonly groupContent?: GroupedVirtuosoProps<any, any>['groupContent']
  readonly groupCounts?: number[]
  readonly isItemLoaded: (index: number) => boolean
  readonly itemCount: number
  readonly itemData: any[]
  readonly itemSize?: number | (() => number)
  readonly itemSpacing?: number
  readonly loadMoreItems: (start: number, end: number) => void
  readonly minimumBatchSize?: number
  readonly onItemsRendered?: (items: any[]) => void
  readonly style?: CSSProperties | undefined
  readonly useGroupedMode?: boolean
}

const InfiniteLoaderList = ({
  useGroupedMode = false,
  Header,
  groupCounts,
  itemData,
  itemCount,
  loadMoreItems,
  isItemLoaded,
  firstItemMargin = 0,
  itemSpacing = 8,
  itemSize,
  focusedListItem,
  minimumBatchSize = INFINITE_LIST_BATCH_LOAD_COUNT,
  children,
  useWindowScroll,
  onItemsRendered,
  disableInitialAutoScroll,
  ...rest
}: InfiniteLoaderListProps) => {
  const listRef = useRef<VirtuosoHandle>(null)

  const [memoizedUnloadedRanges, setMemoizedUnloadedRanges] = useState<
  number[]
  >([])

  useEffect(() => {
    if (listRef.current && R.is(Number, focusedListItem)) {
      listRef.current.scrollToIndex({
        index: focusedListItem,
        behavior: 'auto',
      })
    }
  }, [focusedListItem])

  const isInitiallyLoading = itemData.length === 0 && itemCount > 0

  useEffect(() => {
    if (isInitiallyLoading) {
      setMemoizedUnloadedRanges([])
      if (listRef.current && !disableInitialAutoScroll) {
        listRef.current.scrollToIndex({ index: 0 })
      }
    }
  }, [itemData.length])

  const loadUnloadedRanges = (unloadedRanges: number[]) => {
    for (let i = 0; i < unloadedRanges.length; i += 2) {
      const startIndex = unloadedRanges[i]
      const stopIndex = unloadedRanges[i + 1]

      loadMoreItems(startIndex, stopIndex)
    }
  }

  const rangeChanged = (range: ListRange) => {
    if (!range.startIndex || !range.endIndex || range.startIndex > itemCount) {
      return
    }

    const unloadedRanges = scanForUnloadedRanges({
      isItemLoaded,
      itemCount,
      minimumBatchSize,
      startIndex: range.startIndex,
      stopIndex: range.endIndex,
    })

    if (unloadedRanges.length === 0) {
      return
    }

    const rangesChanged =
      memoizedUnloadedRanges.length !== unloadedRanges.length ||
      memoizedUnloadedRanges.some(
        (value, index) => unloadedRanges[index] !== value,
      )

    if (rangesChanged) {
      setMemoizedUnloadedRanges(unloadedRanges)
      loadUnloadedRanges(unloadedRanges)
    }
  }

  const endReached = () => {
    if (itemData.length > 0 && itemData.length < itemCount) {
      loadMoreItems(itemData.length, itemData.length + minimumBatchSize)
    }
  }

  const itemHeight = itemSize
    ? typeof itemSize === 'function'
      ? itemSize() + itemSpacing
      : itemSize + itemSpacing
    : undefined

  const Component = useGroupedMode ? GroupedVirtuoso : Virtuoso
  const ComponentProps = useGroupedMode
    ? {
      endReached,
      groupCounts,
    }
    : {
      rangeChanged,
    }

  return (
    <Component
      components={{
        Header,
        Footer,
      }}
      data={
        useGroupedMode && itemData.length > 0
          ? undefined
          : resizeArray(itemData, itemCount)
      }
      fixedItemHeight={itemHeight}
      /* eslint-disable-next-line react/no-unstable-nested-components */
      itemContent={(index, item) => (
        <div
          style={{
            height: itemHeight,
            paddingBottom: itemSpacing,
            paddingTop: index === 0 ? firstItemMargin : undefined,
          }}
        >
          {children && children(item, index)}
        </div>
      )}
      itemsRendered={onItemsRendered}
      overscan={500}
      ref={listRef}
      totalCount={itemCount}
      useWindowScroll={useWindowScroll}
      {...ComponentProps}
      {...rest}
    />
  )
}

export default InfiniteLoaderList
